// Map Controls Overlay - Google Maps-like toolbar

import { useState } from 'react';
import { useMapStore } from '../../stores';
import { useNavStore } from '../../stores/navStore';
import { formatDuration, formatRouteDistance } from '../../lib/routing';
import { cn } from '../../lib/utils';
import {
  StreetsIcon,
  LightIcon,
  DarkIcon,
  OsmIcon,
  SatelliteIcon,
  HybridIcon,
  TopoIcon,
  TerrainIcon,
  HeatmapIcon,
  Buildings3DIcon,
} from './MapStyleIcons';
import {
  Layers,
  SlidersHorizontal,
  GripHorizontal,
  X,
  Map as MapIcon,
  List,
  Box,
  Eye,
  RotateCcw,
  Move3D,
  Compass,
  Navigation,
  Keyboard,
  ChevronDown,
  Locate,
  Crosshair,
  Route as RouteIcon,
} from 'lucide-react';

interface MapControlsProps {
  onViewModeChange: (mode: 'map' | 'list' | 'street' | '3d') => void;
  viewMode: 'map' | 'list' | 'street' | '3d';
  onToggleHeatmap: () => void;
  showHeatmap: boolean;
  onToggle3D: () => void;
  is3DEnabled: boolean;
  is3DSupported: boolean;
  onToggleTerrain: () => void;
  isTerrainEnabled: boolean;
  isTerrainSupported: boolean;
}

export function MapControls({
  onViewModeChange,
  viewMode,
  onToggleHeatmap,
  showHeatmap,
  onToggle3D,
  is3DEnabled,
  is3DSupported,
  onToggleTerrain,
  isTerrainEnabled,
  isTerrainSupported,
}: MapControlsProps) {
  const { mapState, setMapState } = useMapStore();
  const { panel, setPanel, whereAmI, route } = useNavStore();
  const controlsOpen = useMapStore((s) => s.controlsOpen);
  const setControlsOpen = useMapStore((s) => s.setControlsOpen);

  const styles = [
    { id: 'streets', label: 'Streets', Icon: StreetsIcon },
    { id: 'light', label: 'Light', Icon: LightIcon },
    { id: 'dark', label: 'Dark', Icon: DarkIcon },
    { id: 'osm', label: 'OSM', Icon: OsmIcon },
    { id: 'satellite', label: 'Satellite', Icon: SatelliteIcon },
    { id: 'hybrid', label: 'Hybrid', Icon: HybridIcon },
    { id: 'outdoors', label: 'Topo', Icon: TopoIcon },
    { id: 'terrain', label: 'Terrain', Icon: TerrainIcon },
  ];

  const viewModes = [
    { id: 'map', label: 'Map', Icon: MapIcon, shortcut: 'M' },
    { id: 'list', label: 'List', Icon: List, shortcut: 'L' },
    { id: '3d', label: '3D', Icon: Box, shortcut: '3' },
    { id: 'street', label: 'Street', Icon: Eye, shortcut: 'S' },
  ];

  return (
    <>
      {/* ---------- Collapsed handle ---------- */}
      <button
        onClick={() => setControlsOpen(!controlsOpen)}
        aria-expanded={controlsOpen}
        aria-label="Map settings"
        title="Map settings"
        className={cn(
          'fixed z-30 flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3.5 py-2',
          'shadow-lg backdrop-blur-md transition-colors hover:bg-white',
          // Above the mobile search bar / location button row.
          'bottom-[4.75rem] lg:bottom-4 left-1/2 -translate-x-1/2'
        )}
      >
        <SlidersHorizontal className="h-4 w-4 text-slate-600" />
        <span className="text-[13px] font-medium text-slate-700">Map &amp; layers</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-slate-400 transition-transform duration-200',
            controlsOpen && 'rotate-180'
          )}
        />
      </button>

      {/* ---------- Expanded sheet ---------- */}
      {controlsOpen && (
        <>
          {/* Scrim closes the sheet on outside tap */}
          <div
            className="fixed inset-0 z-30 bg-black/20"
            onClick={() => setControlsOpen(false)}
            aria-hidden="true"
          />

          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-2 pb-2">
            <div
              role="dialog"
              aria-label="Map settings"
              className={cn(
                'pointer-events-auto w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200',
                'bg-white/95 shadow-2xl backdrop-blur-md',
                // Keep clear of the docked navigation panels on the right.
                panel !== 'none' && 'lg:mr-[25.5rem]',
                'animate-slide-up'
              )}
            >
              {/* Grab bar + title */}
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <GripHorizontal className="h-4 w-4 text-slate-300" />
                  <span className="text-[13px] font-semibold text-slate-800">
                    Map &amp; layers
                  </span>
                </div>
                <button
                  onClick={() => setControlsOpen(false)}
                  aria-label="Close map settings"
                  className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="max-h-[68vh] overflow-y-auto overscroll-contain p-3">
                {/* ---- Map Type ---- */}
        <Section title="Map type" icon={Layers} defaultOpen>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
            {styles.map(({ id, label, Icon }) => {
              const isActive = mapState.style === id;
              return (
                <button
                  key={id}
                  onClick={() => setMapState({ style: id as any })}
                  aria-pressed={isActive}
                  title={label}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1.5 rounded-lg border px-1 py-2 transition-all duration-150',
                    isActive
                      ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700'
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="w-full truncate text-center text-[10px] font-medium leading-none">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ---- View Mode ---- */}
        <Section title="View mode" icon={Eye} defaultOpen>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-4">
            {viewModes.map(({ id, label, Icon, shortcut }) => {
              const isActive = viewMode === id;
              const isDisabled = id === '3d' && !is3DSupported;
              return (
                <button
                  key={id}
                  onClick={() => onViewModeChange(id as any)}
                  disabled={isDisabled}
                  aria-pressed={isActive}
                  title={
                    isDisabled
                      ? '3D buildings unavailable on this map style'
                      : `${label} (${shortcut})`
                  }
                  className={cn(
                    'flex flex-col items-center justify-center gap-1 rounded-lg border px-1 py-2 transition-all duration-150',
                    isActive
                      ? 'border-primary-500 bg-primary-500 text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                    isDisabled && 'cursor-not-allowed opacity-40'
                  )}
                >
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  <span className="text-[10px] font-medium leading-none">{label}</span>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ---- Layers ---- */}
        <Section title="Layers" icon={Layers}>
          <div className="space-y-0.5">
            <ToggleRow
              icon={HeatmapIcon}
              label="Lead heatmap"
              description="Density by score"
              active={showHeatmap}
              onClick={onToggleHeatmap}
            />
            <ToggleRow
              icon={Buildings3DIcon}
              label="3D buildings"
              description={is3DSupported ? 'Extruded volumes' : 'Vector styles only'}
              active={is3DEnabled}
              onClick={onToggle3D}
              disabled={!is3DSupported}
            />
            <ToggleRow
              icon={TerrainIcon}
              label="Terrain"
              description="Elevation relief"
              active={isTerrainEnabled}
              onClick={onToggleTerrain}
              disabled={!isTerrainSupported}
            />
          </div>
        </Section>

        {/* ---- Navigation ---- */}
        <Section title="Navigation" icon={Navigation}>
          <div className="space-y-1.5">
            {/* Where am I */}
            <NavFeatureButton
              icon={Locate}
              label="Where am I?"
              description={
                whereAmI
                  ? whereAmI.address ?? 'Locating your address…'
                  : 'Find your position'
              }
              active={panel === 'location'}
              onClick={() => setPanel(panel === 'location' ? 'none' : 'location')}
            />

            {/* Directions */}
            <NavFeatureButton
              icon={RouteIcon}
              label="Directions"
              description={
                route
                  ? `${formatDuration(route.routes[0].duration)} · ${formatRouteDistance(route.routes[0].distance)}`
                  : 'Plan a route'
              }
              active={panel === 'directions'}
              onClick={() => setPanel(panel === 'directions' ? 'none' : 'directions')}
            />

            <div className="my-1 border-t border-slate-100" />

            {/* Map actions */}
            <ActionButton
              icon={RotateCcw}
              label="Reset view"
              shortcut="N"
              onClick={() => setMapState({ bearing: 0, pitch: 0 })}
            />
            <ActionButton
              icon={Move3D}
              label={mapState.pitch === 0 ? 'Tilt on' : 'Tilt off'}
              shortcut="R"
              active={mapState.pitch !== 0}
              onClick={() => setMapState({ pitch: mapState.pitch === 0 ? 60 : 0 })}
            />
            <ActionButton
              icon={Compass}
              label="North up"
              active={mapState.bearing === 0}
              onClick={() => setMapState({ bearing: 0 })}
            />
            <ActionButton
              icon={Crosshair}
              label="Go to my location"
              shortcut="H"
              onClick={() => {
                if (!navigator.geolocation) return;
                navigator.geolocation.getCurrentPosition((pos) =>
                  setMapState({
                    center: { lat: pos.coords.latitude, lng: pos.coords.longitude },
                    zoom: 16,
                  })
                );
              }}
            />
          </div>
        </Section>
              </div>

              <KeyboardShortcuts />
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared building blocks                                            */
/* ------------------------------------------------------------------ */

function Section({
  title,
  icon: Icon,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
      >
        <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <h3 className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </h3>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && <div className="px-2.5 pb-3">{children}</div>}
    </div>
  );
}

function ToggleRow({
  icon: Icon,
  label,
  description,
  active,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      role="switch"
      aria-checked={active}
      title={disabled ? `${label} — ${description}` : description}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors duration-150',
        active
          ? 'bg-primary-50 hover:bg-primary-100/80'
          : 'hover:bg-slate-50',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent'
      )}
    >
      <Icon
        className={cn(
          'h-[18px] w-[18px] shrink-0 transition-colors',
          active ? 'text-primary-600' : 'text-slate-400'
        )}
      />

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[13px] font-medium leading-tight',
            active ? 'text-primary-800' : 'text-slate-700'
          )}
        >
          {label}
        </span>
        <span className="block truncate text-[11px] leading-tight text-slate-400">
          {description}
        </span>
      </span>

      {/* Switch */}
      <span
        className={cn(
          'relative h-[18px] w-8 shrink-0 rounded-full transition-colors duration-200',
          active ? 'bg-primary-500' : 'bg-slate-300'
        )}
      >
        <span
          className={cn(
            'absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow transition-transform duration-200',
            active ? 'translate-x-[16px]' : 'translate-x-[2px]'
          )}
        />
      </span>
    </button>
  );
}

function NavFeatureButton({
  icon: Icon,
  label,
  description,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all duration-150',
        active
          ? 'border-primary-500 bg-primary-50'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      )}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          active ? 'bg-primary-500' : 'bg-slate-100'
        )}
      >
        <Icon
          className={cn(
            'h-4 w-4',
            active ? 'text-white' : 'text-slate-500'
          )}
        />
      </div>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[13px] font-medium leading-tight',
            active ? 'text-primary-800' : 'text-slate-700'
          )}
        >
          {label}
        </span>
        <span className="block truncate text-[11px] leading-tight text-slate-400">
          {description}
        </span>
      </span>
    </button>
  );
}

function ActionButton({
  icon: Icon,
  label,
  shortcut,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  shortcut?: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={shortcut ? `${label} (${shortcut})` : label}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-all duration-150',
        active
          ? 'border-primary-300 bg-primary-50 text-primary-800'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 shrink-0',
          active ? 'text-primary-600' : 'text-slate-400'
        )}
      />
      <span className="min-w-0 flex-1 truncate text-[12px] font-medium leading-none">
        {label}
      </span>
      {shortcut && (
        <kbd className="shrink-0 rounded border border-slate-200 bg-slate-100 px-1 py-0.5 font-mono text-[9px] font-medium text-slate-500">
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

function KeyboardShortcuts() {
  const [open, setOpen] = useState(false);

  const groups = [
    {
      title: 'Map',
      items: [
        { keys: ['+', '-'], label: 'Zoom' },
        { keys: ['↑', '↓', '←', '→'], label: 'Pan' },
        { keys: ['N'], label: 'North up' },
        { keys: ['R'], label: 'Tilt' },
      ],
    },
    {
      title: 'Views',
      items: [
        { keys: ['M'], label: 'Map' },
        { keys: ['L'], label: 'List' },
        { keys: ['3'], label: '3D' },
        { keys: ['S'], label: 'Street' },
      ],
    },
    {
      title: 'Other',
      items: [
        { keys: ['G'], label: 'Where am I' },
        { keys: ['D'], label: 'Directions' },
        { keys: ['⌘', 'K'], label: 'Search' },
        { keys: ['Esc'], label: 'Close' },
      ],
    },
  ];

  return (
    <div className="shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white/95 shadow-lg backdrop-blur-md">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
      >
        <Keyboard className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider">
          Shortcuts
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-100 px-3 py-3">
          {groups.map((group) => (
            <div key={group.title}>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {group.title}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate text-[11px] text-slate-600">{item.label}</span>
                    <span className="flex shrink-0 items-center gap-0.5">
                      {item.keys.map((k) => (
                        <kbd
                          key={k}
                          className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[9px] font-medium text-slate-600"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MapControls;